import React, { useState, useEffect, useRef, useCallback } from 'react';
import { modalitiesAPI, bodyPartsAPI, instructionsAPI, reportsAPI, feedbackAPI } from '../../services/api';
import { useSnackbar } from '../Snackbar';
import FilterDropdown from '../common/FilterDropdown';
import './ReportConverter.css';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';

const FeedbackIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const AttachIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

function applyTechniqueToRaw(rawText, techniqueText) {

  const techniqueRe = /^TECHNIQUE[\s:\xa0].*$/im;
  let stripped = (rawText || '').replace(techniqueRe, '');
  stripped = stripped.replace(/\n[ \t]*\n[ \t]*\n+/g, '\n\n').trim();

  if (!techniqueText) return stripped;

  const line = `TECHNIQUE:  ${techniqueText}`;

  // Preferred position: right under COMPARISON.
  const comparisonRe = /^(COMPARISON[\s:\xa0].*)$/im;
  if (comparisonRe.test(stripped)) {
    return stripped.replace(comparisonRe, (m) => `${m}\n${line}`);
  }

  const examRe = /^(Examination[\s:\xa0].*)$/im;
  if (examRe.test(stripped)) {
    return stripped.replace(examRe, (m) => `${m}\n${line}`);
  }

  return stripped ? `${stripped}\n${line}` : line;
}

function renderReport(text) {
  if (!text) return '';
  // Interleave a zero-width space (U+200B) between each regular space in a
  // run of 2+ spaces. This breaks up the literal "consecutive space
  // character" sequence that Word's paste handler and autocorrect can
  // silently collapse back down to a single space, while remaining
  // completely invisible so it still renders as normal double-spacing.
  const escHtml = (s) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/ {2,}/g, (m) => Array(m.length).fill(' ').join('\u200B'))
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  const lines = text.split('\n');
  let html = ''; let i = 0;
  while (i < lines.length) {
    if (lines[i].trim() === '') {
      while (i < lines.length && lines[i].trim() === '') i++;
      if (i < lines.length) html += '<div style="height:5px"></div>';
    } else {
      html += `<div class="rr-line">${escHtml(lines[i].trim())}</div>`;
      i++;
    }
  }
  return html;
}

// Converts a contentEditable element's actual DOM content back into our
// plain markdown-formatted text (**bold**, exact spacing preserved). This
// MUST be used instead of `.innerText`, which reflects the browser's
// *rendered* layout — it silently collapses runs of multiple spaces down to
// one, and discards all formatting (bold, etc.) entirely. Walking the DOM
// with `.textContent` on each text node preserves the real underlying
// characters (including any literal spaces or non-breaking spaces) exactly
// as they exist in the document, with no rendering-driven collapsing.
function editableElementToMarkdownText(el) {
  if (!el) return '';
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const tag = node.tagName.toLowerCase();
    let inner = '';
    for (const child of node.childNodes) inner += walk(child);
    if (tag === 'b' || tag === 'strong') return `**${inner}**`;
    if (tag === 'br') return '\n';
    if (tag === 'div' || tag === 'p') return inner + '\n';
    return inner;
  };
  let out = '';
  for (const child of el.childNodes) out += walk(child);
  // Un-interleave: remove the zero-width spaces, leaving the regular spaces
  // exactly as they were, and collapse trailing-newline artifacts from
  // block elements.
  out = out.replace(/\u200B/g, '').replace(/\n{3,}/g, '\n\n').replace(/\n+$/, '');
  return out;
}

async function copyRichText(text) {
  // Same zero-width-space interleaving as renderReport — see that comment
  // for why. Applied here too since this is the clipboard-writing fallback
  // path used when there's no live rendered DOM element to copy from.
  const preserveSpacing = (s) => s.replace(/ {2,}/g, (m) => Array(m.length).fill(' ').join('\u200B'));
  const escHtml = (s) =>
    preserveSpacing(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  const CONTENT = 'margin:0;padding:0;line-height:1.15;font-size:10pt;font-family:Tahoma;';
  const SPACER = 'margin:0;padding:0;line-height:1.0;font-size:1pt;font-family:Tahoma;';
  const lines = text.split('\n');
  let body = ''; let i = 0;
  while (i < lines.length) {
    if (lines[i].trim() === '') {
      while (i < lines.length && lines[i].trim() === '') i++;
      if (i < lines.length) body += `<p style="${SPACER}"> </p>`;
    } else {
      body += `<p style="${CONTENT}">${escHtml(lines[i].trim())}</p>`;
      i++;
    }
  }
  const html = `<html><body style="font-family:Tahoma;font-size:10pt;margin:0;padding:0;">${body}</body></html>`;
  const plain = preserveSpacing(text).replace(/\*\*(.*?)\*\*/g, '$1');
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      }),
    ]);
  } catch { await navigator.clipboard.writeText(plain); }
}

const Toolbar = ({ editorRef }) => {
  const exec = (cmd, val = null) => {
    if (editorRef?.current) editorRef.current.focus();
    document.execCommand(cmd, false, val);
  };

  const [fontFamily, setFontFamily] = useState('Tahoma');
  const [fontSize, setFontSize] = useState('3');

  const fonts = ['Tahoma', 'Arial', 'Times New Roman', 'Calibri', 'Courier New', 'Georgia', 'Verdana'];
  const sizes = [
    { val: '1', label: '8' }, { val: '2', label: '10' }, { val: '3', label: '12' },
    { val: '4', label: '14' }, { val: '5', label: '18' }, { val: '6', label: '24' }, { val: '7', label: '36' },
  ];

  return (
    <div className="doc-toolbar">
      <div className="tb-row">
        <button className="tb-btn" title="Undo" onClick={() => exec('undo')}><UndoIcon sx={{ fontSize: 15 }} /></button>
        <button className="tb-btn" title="Redo" onClick={() => exec('redo')}><RedoIcon sx={{ fontSize: 15 }} /></button>
        <div className="tb-sep" />

        <select className="tb-select tb-font" title="Font family"
          value={fontFamily} onChange={e => { setFontFamily(e.target.value); exec('fontName', e.target.value); }}>
          {fonts.map(f => <option key={f} value={f}>{f}</option>)}
        </select>

        <select className="tb-select tb-size" title="Font size"
          value={fontSize} onChange={e => { setFontSize(e.target.value); exec('fontSize', e.target.value); }}>
          {sizes.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
        </select>

        <div className="tb-sep" />
        <button className="tb-btn" title="Bold (Ctrl+B)" onClick={() => exec('bold')}><FormatBoldIcon sx={{ fontSize: 15 }} /></button>
        <button className="tb-btn" title="Italic (Ctrl+I)" onClick={() => exec('italic')}><FormatItalicIcon sx={{ fontSize: 15 }} /></button>
        <button className="tb-btn" title="Underline (Ctrl+U)" onClick={() => exec('underline')}><FormatUnderlinedIcon sx={{ fontSize: 15 }} /></button>

        <div className="tb-sep" />
        <button className="tb-btn" title="Align Left" onClick={() => exec('justifyLeft')}><FormatAlignLeftIcon sx={{ fontSize: 15 }} /></button>
        <button className="tb-btn" title="Align Center" onClick={() => exec('justifyCenter')}><FormatAlignCenterIcon sx={{ fontSize: 15 }} /></button>
        <button className="tb-btn" title="Align Right" onClick={() => exec('justifyRight')}><FormatAlignRightIcon sx={{ fontSize: 15 }} /></button>
        <button className="tb-btn" title="Justify" onClick={() => exec('justifyFull')}><FormatAlignJustifyIcon sx={{ fontSize: 15 }} /></button>

        <div className="tb-sep" />
        <button className="tb-btn" title="Bullet List" onClick={() => exec('insertUnorderedList')}><FormatListBulletedIcon sx={{ fontSize: 15 }} /></button>
        <button className="tb-btn" title="Numbered List" onClick={() => exec('insertOrderedList')}><FormatListNumberedIcon sx={{ fontSize: 15 }} /></button>
        <button className="tb-btn" title="Indent" onClick={() => exec('indent')}><FormatIndentIncreaseIcon sx={{ fontSize: 15 }} /></button>
        <button className="tb-btn" title="Outdent" onClick={() => exec('outdent')}><FormatIndentDecreaseIcon sx={{ fontSize: 15 }} /></button>
      </div>
    </div>
  );
};

const ReportConverter = () => {
  const snack = useSnackbar();
  const [modalities, setModalities] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [form, setForm] = useState(() => {
    try {
      const saved = sessionStorage.getItem('converter_form');
      return saved ? JSON.parse(saved) : { report_type: '', modality_id: '', body_part_id: '', technique_id: '', raw_report: '' };
    } catch { return { report_type: '', modality_id: '', body_part_id: '', technique_id: '', raw_report: '' }; }
  });
  const [finalReport, setFinalReport] = useState(() => {
    try { return sessionStorage.getItem('converter_final') || ''; } catch { return ''; }
  });
  // Displayed/editable text. Starts equal to the AI's finalReport; once the
  // user edits and saves, this holds their correction while `finalReport`
  // keeps the untouched AI baseline for later diffing.
  const [displayReport, setDisplayReport] = useState(() => {
    try {
      return sessionStorage.getItem('converter_corrected')
        || sessionStorage.getItem('converter_final')
        || '';
    } catch { return ''; }
  });
  const [savedReportId, setSavedReportId] = useState(() => {
    try { return sessionStorage.getItem('converter_report_id') || null; } catch { return null; }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackFiles, setFeedbackFiles] = useState([]);
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);

  const editRef = useRef(null);
  const rawRef = useRef(null);
  const renderedRef = useRef(null);
  const feedbackFileRef = useRef(null);

  useEffect(() => {
    modalitiesAPI.getAll().then(r => setModalities(r.data || [])).catch(() => { });
    bodyPartsAPI.getAll().then(r => setBodyParts(r.data || [])).catch(() => { });
    instructionsAPI.getAll({ type: 'technique' }).then(r => setTechniques(r.data || [])).catch(() => { });
  }, []);

  const techniqueOptions = techniques.filter(
    t => !t.modality_id || t.modality_id === form.modality_id
  );

  const handleTechniqueChange = useCallback((techniqueId) => {
    const tech = techniques.find(t => t.id === techniqueId);
    setForm(f => {
      const newRaw = applyTechniqueToRaw(f.raw_report, tech?.technique_text || '');
      const n = { ...f, technique_id: techniqueId, raw_report: newRaw };
      try { sessionStorage.setItem('converter_form', JSON.stringify(n)); } catch { }
      if (rawRef.current) rawRef.current.innerText = newRaw;
      return n;
    });
  }, [techniques]);

  useEffect(() => {
    if (rawRef.current && form.raw_report && !rawRef.current.innerText) {
      rawRef.current.innerText = form.raw_report;
    }
  }, []);

  const handleConvert = useCallback(async () => {
    if (!form.report_type) { snack('Please select a type first.', 'warning'); return; }
    if (!form.modality_id) { snack('Please select a modality first.', 'warning'); return; }
    if (!form.body_part_id) { snack('Please select a body part first.', 'warning'); return; }
    if (!form.raw_report.trim()) { snack('Please paste a raw report first.', 'warning'); return; }
    setLoading(true);
    setFinalReport(''); setSavedReportId(null); setIsEdited(false); setIsEditing(false);
    setFeedback(''); setFeedbackSaved(false); setFeedbackFiles([]);
    try {
      const { data } = await reportsAPI.convert(form);
      setFinalReport(data.final_report);
      setDisplayReport(data.final_report);
      try {
        sessionStorage.setItem('converter_final', data.final_report);
        sessionStorage.removeItem('converter_corrected'); // fresh generation clears any prior correction
      } catch { }
      setSavedReportId(data.report.id);
      try { sessionStorage.setItem('converter_report_id', data.report.id); } catch { }
      snack('Report converted successfully!', 'success');
    } catch (err) {
      snack(err.response?.data?.error || 'Conversion failed. Please try again.', 'error', 6000);
    } finally { setLoading(false); }
  }, [form, snack]);

  const handleCopy = useCallback(async () => {
    if (!displayReport) return;
    try {
      const sourceEl = isEditing ? editRef.current : renderedRef.current;
      const innerHTML = sourceEl?.innerHTML || '';
      const innerText = sourceEl
        ? editableElementToMarkdownText(sourceEl).replace(/\*\*(.*?)\*\*/g, '$1')
        : displayReport.replace(/\*\*(.*?)\*\*/g, '$1');

      if (innerHTML) {
        const wordHtml = `<html><body style="font-family:Tahoma;font-size:10pt;margin:0;padding:0;">${innerHTML}</body></html>`;
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([wordHtml], { type: 'text/html' }),
            'text/plain': new Blob([innerText], { type: 'text/plain' }),
          }),
        ]);
      } else {
        await copyRichText(displayReport);
      }
      setCopied(true);
      snack('Copied! Paste into Word to keep all formatting.', 'success');
      setTimeout(() => setCopied(false), 2500);
    } catch { snack('Copy failed. Try again.', 'error'); }
  }, [displayReport, isEditing, snack]);

  const handleSave = useCallback(async () => {
    if (!savedReportId) return;
    setSaving(true);
    try {
      const activeEl = isEditing ? editRef.current : renderedRef.current;
      const content = activeEl ? editableElementToMarkdownText(activeEl) : displayReport;

      await reportsAPI.update(savedReportId, { corrected_report: content, status: 'completed' });
      setDisplayReport(content);
      try { sessionStorage.setItem('converter_corrected', content); } catch { }
      snack('Saved and marked as completed!', 'success');
      setIsEdited(false); setIsEditing(false);
    } catch { snack('Save failed. Try again.', 'error'); }
    finally { setSaving(false); }
  }, [savedReportId, displayReport, isEditing, snack]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setIsEdited(false);
  }, []);

  const handleFeedbackFiles = e => {
    const selected = Array.from(e.target.files);
    const valid = selected.filter(f => f.size <= 20 * 1024 * 1024);
    if (valid.length < selected.length) snack('Some files exceed 20MB limit', 'warning');
    setFeedbackFiles(prev => [...prev, ...valid].slice(0, 5));
  };

  const removeFeedbackFile = idx => setFeedbackFiles(f => f.filter((_, i) => i !== idx));

  const handleFeedbackSubmit = useCallback(async () => {
    if (!feedback.trim()) return;
    if (!savedReportId) {
      snack('Convert a report first — feedback needs a report to attach to.', 'warning');
      return;
    }
    setFeedbackSaving(true);
    try {
      const fd = new FormData();
      fd.append('message', feedback.trim());
      fd.append('report_id', savedReportId);
      fd.append('status', 'open');
      feedbackFiles.forEach(f => fd.append('files', f));
      await feedbackAPI.submit(fd);
      setFeedbackSaved(true);
      setFeedbackFiles([]);
      snack('Feedback saved with this report!', 'success');
    } catch { snack('Failed to save feedback. Try again.', 'error'); }
    finally { setFeedbackSaving(false); }
  }, [savedReportId, feedback, feedbackFiles, snack]);

  const handleFeedbackClear = useCallback(() => {
    setFeedback('');
    setFeedbackFiles([]);
    setFeedbackSaved(false);
  }, []);

  const handleClear = useCallback(() => {
    setForm({ report_type: '', modality_id: '', body_part_id: '', technique_id: '', raw_report: '' });
    setFinalReport('');
    setDisplayReport('');
    if (rawRef.current) rawRef.current.innerText = '';
    try {
      sessionStorage.removeItem('converter_form');
      sessionStorage.removeItem('converter_final');
      sessionStorage.removeItem('converter_corrected');
      sessionStorage.removeItem('converter_report_id');
    } catch { }
    setSavedReportId(null); setIsEdited(false); setIsEditing(false);
    setFeedback(''); setFeedbackSaved(false); setFeedbackFiles([]);
    snack('Cleared.', 'info', 2000);
  }, [snack]);

  return (
    <div className="converter-page">
      <div className="converter-grid">

        {/* ── LEFT: Raw Input ── */}
        <div className="converter-panel">
          <div className="panel-header">
            <p className="panel-title">
              <span className="panel-title-dot" />
              RAW REPORT INPUT
            </p>
            <div className="input-actions">
              <button className="btn-clear" onClick={handleClear} disabled={loading}>Clear</button>
              <button
                className="btn-convert"
                onClick={handleConvert}
                disabled={loading || !form.report_type || !form.modality_id || !form.body_part_id}
                title={(!form.report_type || !form.modality_id || !form.body_part_id) ? 'Select Type, Modality and Body Part to enable Generate Report' : undefined}
              >
                {loading ? <><span className="spinner" /> Generating…</> : <>Generate Report</>}
              </button>
            </div>
          </div>

          <div className="panel-body">
            <div className="dropdowns-row">
              <div>
                <span className="field-label">Type</span>
                <FilterDropdown
                  value={form.report_type}
                  options={[
                    { value: '', label: 'Select Type' },
                    { value: 'hospital', label: 'Hospital' },
                    { value: 'non_hospital', label: 'Non Hospital' },
                  ]}
                  onChange={v => setForm(f => { const n = { ...f, report_type: v }; try { sessionStorage.setItem('converter_form', JSON.stringify(n)); } catch { } return n; })}
                  align="left"
                />
              </div>
              <div>
                <span className="field-label">Modality</span>
                <FilterDropdown
                  value={form.modality_id}
                  options={[{ value: '', label: 'Select modality' }, ...modalities.map(m => ({ value: m.id, label: m.name }))]}
                  onChange={v => setForm(f => { const n = { ...f, modality_id: v, technique_id: '' }; try { sessionStorage.setItem('converter_form', JSON.stringify(n)); } catch { } return n; })}
                  align="left"
                />
              </div>
              <div>
                <span className="field-label">Body Part</span>
                <FilterDropdown
                  value={form.body_part_id}
                  options={[{ value: '', label: 'Select body part' }, ...bodyParts.map(b => ({ value: b.id, label: b.name }))]}
                  onChange={v => setForm(f => { const n = { ...f, body_part_id: v }; try { sessionStorage.setItem('converter_form', JSON.stringify(n)); } catch { } return n; })}
                  align="left"
                  searchable
                />
              </div>
              <div>
                <span className="field-label">Technique</span>
                <FilterDropdown
                  value={form.technique_id}
                  options={[
                    {
                      value: '',
                      label: techniqueOptions.length
                        ? 'Select technique…'
                        : (form.modality_id ? 'No techniques available' : 'Select modality first'),
                    },
                    ...techniqueOptions.map(t => ({ value: t.id, label: t.name })),
                  ]}
                  onChange={handleTechniqueChange}
                  align="right"
                />
              </div>
            </div>

            <div className="raw-textarea-label">
              <span>Raw Report</span>
              {form.raw_report.length > 0 && <span className="char-count">{form.raw_report.length.toLocaleString()} chars</span>}
            </div>

            <Toolbar editorRef={rawRef} />

            <div
              ref={rawRef}
              className="report-textarea raw-report-ta doc-editor"
              contentEditable
              suppressContentEditableWarning
              data-placeholder="Paste the raw radiology report here..."
              onPaste={e => {
                e.preventDefault();
                const clipData = e.clipboardData;

                const text = clipData.getData('text/plain');
                if (text) {
                  const selection = window.getSelection();
                  if (selection.rangeCount) {
                    selection.deleteFromDocument();
                    const range = selection.getRangeAt(0);
                    const lines = text.split('\n');
                    const frag = document.createDocumentFragment();
                    lines.forEach((line, i) => {
                      frag.appendChild(document.createTextNode(line));
                      if (i < lines.length - 1) frag.appendChild(document.createElement('br'));
                    });
                    range.insertNode(frag);
                    range.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(range);
                  }
                }

                setTimeout(() => {
                  const v = rawRef.current?.innerText || '';
                  setForm(f => {
                    let newRaw = v;
                    if (f.technique_id) {
                      const tech = techniques.find(t => t.id === f.technique_id);
                      if (tech?.technique_text) newRaw = applyTechniqueToRaw(v, tech.technique_text);
                    }
                    const n = { ...f, raw_report: newRaw };
                    try { sessionStorage.setItem('converter_form', JSON.stringify(n)); } catch { }
                    if (rawRef.current && newRaw !== v) rawRef.current.innerText = newRaw;
                    return n;
                  });
                }, 0);
              }}
              onInput={e => {
                const v = e.currentTarget.innerText;
                setForm(f => { const n = { ...f, raw_report: v }; try { sessionStorage.setItem('converter_form', JSON.stringify(n)); } catch { } return n; });
              }}
            />
          </div>
        </div>

        {/* ── RIGHT: Output ── */}
        <div className="converter-panel">
          <div className="panel-header">
            <p className="panel-title">
              <span className="panel-title-dot green" />
              FINAL REPORT OUTPUT
              {isEdited && <span className="edited-badge">● Unsaved</span>}
            </p>
            <div className="output-actions">
              {finalReport && !isEditing && (
                <button className="btn-action" onClick={() => { setIsEditing(true); setTimeout(() => editRef.current?.focus(), 50); }}>
                  <EditIcon sx={{ fontSize: 14 }} /> Edit
                </button>
              )}
              {finalReport && isEditing && (
                <button className="btn-action" onClick={handleCancelEdit}>
                  <CloseIcon sx={{ fontSize: 14 }} /> Cancel
                </button>
              )}
              {finalReport && (
                <button className="btn-action" onClick={handleCopy}>
                  {copied ? <><CheckIcon sx={{ fontSize: 14 }} /> Copied!</> : <><ContentCopyIcon sx={{ fontSize: 14 }} /> Copy</>}
                </button>
              )}
              {finalReport && (
                <button className="btn-action save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : <><SaveIcon sx={{ fontSize: 14 }} /> Save</>}
                </button>
              )}
            </div>
          </div>

          <Toolbar editorRef={editRef} />

          <div className="panel-body">
            {loading && (
              <div className="loading-state">
                <svg width="48" height="48" viewBox="0 0 48 48" className="loading-ring">
                  <circle cx="24" cy="24" r="20" className="loading-ring-track" />
                  <circle cx="24" cy="24" r="20" className="loading-ring-arc" />
                </svg>
                <div className="loading-text-block">
                  <p className="loading-title">Generating report</p>
                  <p className="loading-sub">This usually takes a few seconds</p>
                </div>
                <div className="loading-steps">
                  <div className="loading-step-row done"><CheckIcon className="step-check" sx={{ fontSize: 15 }} /><span>Instructions loaded</span></div>
                  <div className="loading-step-row done"><CheckIcon className="step-check" sx={{ fontSize: 15 }} /><span>Applying QC rules</span></div>
                  <div className="loading-step-row active"><div className="step-spinner" /><span>Formatting output</span></div>
                </div>
              </div>
            )}

            {!loading && finalReport && !isEditing && (
              <div
                ref={renderedRef}
                className="report-rendered doc-editor"
                contentEditable
                suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: renderReport(displayReport) }}
                onInput={() => setIsEdited(true)}
              />
            )}

            {!loading && finalReport && isEditing && (
              <>
                <div
                  ref={editRef}
                  className="report-textarea final-report doc-editor"
                  contentEditable
                  suppressContentEditableWarning
                  dangerouslySetInnerHTML={{ __html: renderReport(displayReport) }}
                  onInput={() => setIsEdited(true)}
                />
                <p className="edit-hint">Edit above · Click Save to mark as completed, or Cancel to discard changes</p>
              </>
            )}

            {!loading && !finalReport && (
              <div className="empty-state">
                <div className="empty-icon-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <p className="empty-title">No report yet</p>
                <div className="empty-steps">
                  <div className="empty-step"><span className="step-num">1</span><span>Select modality &amp; body part</span></div>
                  <div className="empty-step"><span className="step-num">2</span><span>Paste the raw report</span></div>
                  <div className="empty-step"><span className="step-num">3</span><span>Click Generate Report</span></div>
                </div>
              </div>
            )}
          </div>

          <div className="feedback-section">
            <div className="feedback-header">
              <span className="feedback-header-icon"><FeedbackIcon /></span>
              <span>Feedback on this report</span>
              {feedbackSaved && <span className="feedback-saved-badge">✓ Saved</span>}
            </div>

            <textarea
              className="feedback-textarea"
              placeholder="Add any notes or feedback about this report (e.g. QC issues, suggestions)..."
              value={feedback}
              onChange={e => { setFeedback(e.target.value); setFeedbackSaved(false); }}
              rows={3}
            />

            <div className="feedback-dropzone" onClick={() => feedbackFileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleFeedbackFiles({ target: { files: e.dataTransfer.files } }); }}>
              <input ref={feedbackFileRef} type="file" multiple hidden
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                onChange={handleFeedbackFiles}
              />
              <span className="feedback-dz-icon"><AttachIcon /></span>
              <span className="feedback-dz-text">Attach files <span className="feedback-dz-hint">· PDF, Word, Images · max 20MB · up to 5</span></span>
            </div>

            {feedbackFiles.length > 0 && (
              <div className="feedback-files-grid">
                {feedbackFiles.map((f, i) => (
                  <div key={i} className="feedback-file-chip">
                    <span className="feedback-chip-name">{f.name.length > 18 ? f.name.slice(0, 15) + '...' : f.name}</span>
                    <button className="feedback-chip-rm" onClick={() => removeFeedbackFile(i)}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="feedback-actions">
              <button
                className="btn-action save"
                onClick={handleFeedbackSubmit}
                disabled={feedbackSaving || !feedback.trim()}
              >
                {feedbackSaving ? 'Saving…' : 'Submit feedback'}
              </button>
              <button
                type="button"
                className="btn-clear"
                onClick={handleFeedbackClear}
                disabled={feedbackSaving || (!feedback && feedbackFiles.length === 0)}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ReportConverter;